import { RESPONSE_PROMISE_SV } from "@/content/response-times";
import { createServerFn } from "@tanstack/react-start";
import { getRequest } from "@tanstack/react-start/server";
import { z } from "zod";
import { persistLead, captureBlockedLead } from "@/lib/lead-fallback.server";
import { confirmationEmail, internalLeadEmail, LEAD_INBOX, LEAD_NOTIFY, sendEmail } from "@/lib/email/resend.server";
import { checkRateLimit, getClientIp } from "@/lib/rate-limit.server";

const Schema = z.object({
  magnet_slug: z.string().min(1).max(80).regex(/^[a-z0-9-]+$/),
  email: z.string().email().max(255),
  company: z.string().max(120).optional().nullable(),
  audience_slug: z.string().max(40).optional().nullable(),
  source_path: z.string().max(255).optional().nullable(),
  user_agent: z.string().max(500).optional().nullable(),
});

export const requestAudienceMagnet = createServerFn({ method: "POST" })
  .inputValidator((input: unknown) => Schema.parse(input))
  .handler(async ({ data }) => {
    const ip = getClientIp(getRequest());
    if (!checkRateLimit({ scope: "audience-magnet", keys: [ip, data.email], limit: 5, windowMs: 60 * 60 * 1000 })) {
      await captureBlockedLead({
        flow: "audience-magnet",
        reason: "rate-limit",
        email: data.email,
        company: data.company ?? null,
        source_path: data.source_path ?? null,
        user_agent: data.user_agent ?? null,
        extra: { magnet_slug: data.magnet_slug, audience_slug: data.audience_slug },
      });
      return { ok: false as const };
    }
    try {
      await persistLead(
        "lead_magnet_requests",
        {
          magnet_slug: data.magnet_slug,
          email: data.email,
          company: data.company ?? null,
          source_path: data.source_path ?? null,
          user_agent: data.user_agent ?? null,
          funnel_id: data.audience_slug ?? null,
        },
        {
          source: "audience-magnet",
          email: data.email,
          company: data.company ?? null,
          source_path: data.source_path ?? null,
        },
      );
    } catch (e) {
      console.error("[audience-magnet] unexpected:", e);
    }
    const internal = internalLeadEmail({
      subject: `Lead magnet: ${data.magnet_slug} — ${data.email}`,
      heading: `Nytt lead: ${data.magnet_slug}`,
      fields: {
        "Magnet": data.magnet_slug,
        "E-post": data.email,
        "Företag": data.company,
        "Målgrupp": data.audience_slug,
        "Källa": data.source_path,
      },
    });
    const confirm = confirmationEmail({
      intro: `Tack — vi har tagit emot din förfrågan och återkommer med materialet. ${RESPONSE_PROMISE_SV}`,
    });
    await Promise.allSettled([
      sendEmail({ to: LEAD_NOTIFY, subject: internal.subject, html: internal.html, replyTo: data.email }),
      sendEmail({ to: data.email, subject: confirm.subject, html: confirm.html, replyTo: LEAD_INBOX }),
    ]);
    return { ok: true as const };
  });
